import React, { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/useApi';
import { useEffect } from 'react';
import PremiumTooltip from '../common/PremiumTooltip';
import FloatingManualButton from '../common/FloatingManualButton';

const AdminLayout = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [enquiryCount, setEnquiryCount] = useState(0);
    const { get } = useApi();
    const dropdownRef = useRef(null);

    // Auto-close menu on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Auto-close menu on navigation
    useEffect(() => {
        setIsUserMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const fetchCount = async () => {
            try {
                const res = await get('/api/enquiries/admin/count/');
                setEnquiryCount(res.count);
            } catch (err) {
                console.error('Failed to fetch enquiry count');
            }
        };
        fetchCount();
        const interval = setInterval(fetchCount, 300000); // Poll every 5 mins
        return () => clearInterval(interval);
    }, [get]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const navigationGroups = [
        {
            title: 'Core Systems',
            items: [
                { path: '/admin', label: 'Dashboard', icon: 'fa-gauge-high' },
                { path: '/admin/users', label: 'Users', icon: 'fa-users-gear' },
                { path: '/admin/houses', label: 'House Registry', icon: 'fa-landmark' },
                { path: '/admin/discipline', label: 'Character Items', icon: 'fa-scale-balanced' },
            ]
        },
        {
            title: 'Academics',
            items: [
                { path: '/admin/grades', label: 'Assessment Registry', icon: 'fa-award' },
                { path: '/admin/courses', label: 'Learning Areas', icon: 'fa-book' },
                { path: '/admin/curriculum', label: 'Curriculum', icon: 'fa-sitemap' },
            ]
        },
        {
            title: 'Activities',
            items: [
                { path: '/admin/events', label: 'Events', icon: 'fa-calendar-check' },
                { path: '/admin/clubs', label: 'Extracurriculars', icon: 'fa-flag' },
            ]
        },
        {
            title: 'Operations',
            items: [
                { path: '/admin/finance', label: 'Finance', icon: 'fa-wallet' },
            ]
        }
    ];

    const isActive = (path) => {
        if (path === '/admin') {
            return location.pathname === '/admin';
        }
        return location.pathname.startsWith(path);
    };

    const displayName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Guest' : 'Guest';
    const initials = displayName
        .split(' ')
        .map((token) => token.charAt(0))
        .join('')
        .slice(0, 2)
        .toUpperCase();

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            {/* Main Header */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
                <div className="px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            className="p-2 hover:bg-slate-50 rounded-lg text-gray-400 transition-colors hidden lg:block"
                            title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                        >
                            <i className={`fas fa-bars-staggered transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : ''}`}></i>
                        </button>
                        <Link to="/admin" className="flex items-center space-x-3 group mr-8">
                            <img src="/kianda-school-logo-removebg-preview.png" alt="Kianda School" className="h-10 w-auto object-contain transition-transform group-hover:scale-110" />
                            <div className={`${isSidebarCollapsed ? 'lg:hidden' : 'block'} transition-all duration-300 overflow-hidden whitespace-nowrap`}>
                                <h1 className="text-lg font-black text-[#18216D] leading-none uppercase tracking-tighter">Kianda School</h1>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FFC425]">Admin Portal</p>
                            </div>
                        </Link>

                        {/* Top Navigation - Feedback Hub */}
                        <div className="hidden md:flex items-center border-l border-slate-100 pl-8 ml-2">
                            <Link 
                                to="/admin/enquiries" 
                                className={`flex items-center gap-3 px-6 py-2 rounded-2xl transition-all duration-300 group ${
                                    isActive('/admin/enquiries') 
                                    ? 'bg-[#18216D] text-white shadow-xl shadow-indigo-900/20' 
                                    : 'text-slate-400 hover:text-[#18216D] hover:bg-slate-50'
                                }`}
                            >
                                <div className="relative">
                                    <i className={`fas fa-comments text-base ${isActive('/admin/enquiries') ? 'text-white' : 'text-slate-300 group-hover:text-[#18216D]'}`}></i>
                                    {enquiryCount > 0 && !isActive('/admin/enquiries') && (
                                        <span className="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 bg-[#FFC425] border-2 border-white rounded-full"></span>
                                    )}
                                </div>
                                <span className="text-[10px] font-black uppercase tracking-widest">Feedback Hub</span>
                                {enquiryCount > 0 && (
                                    <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-[#FFC425] text-[#18216D] shadow-sm ml-1 group-hover:scale-110 transition-transform">
                                        {enquiryCount}
                                    </span>
                                )}
                            </Link>
                        </div>
                    </div>

                    <div className="flex items-center space-x-6">
                        <button className="text-gray-400 hover:text-gray-600 transition-colors relative group">
                            <i className="far fa-bell text-xl"></i>
                            <span className="absolute top-0 right-0 h-2 w-2 bg-[#FFC425] rounded-full border-2 border-white"></span>
                        </button>

                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                className="flex items-center gap-3 px-2 py-1.5 rounded-full border border-gray-200 hover:border-[#FFC425]/30 hover:bg-slate-50 transition-all"
                            >
                                <span className="h-8 w-8 rounded-full bg-[#18216D] text-white flex items-center justify-center text-xs font-bold shadow-lg shadow-indigo-900/10">
                                    {initials}
                                </span>
                                <div className="text-left hidden sm:block">
                                    <p className="text-sm font-black text-gray-900 leading-tight">{displayName}</p>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">System Authority</p>
                                </div>
                                <i className={`fas fa-chevron-${isUserMenuOpen ? 'up' : 'down'} text-[10px] text-gray-400`}></i>
                            </button>

                            {isUserMenuOpen && (
                                <div className="absolute right-0 mt-3 w-56 bg-white rounded-[2rem] shadow-2xl border border-gray-100 overflow-hidden py-2 transform transition-all animate-in fade-in slide-in-from-top-2 z-[60]">
                                    <div className="px-6 py-4 border-b border-gray-50 bg-slate-50/50">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Active Operator</p>
                                        <p className="text-xs font-bold text-gray-900 truncate mt-1">{user?.email}</p>
                                    </div>
                                    <Link to="/profile" className="flex items-center space-x-3 px-6 py-3 text-sm font-bold text-gray-700 hover:bg-slate-50 transition-colors">
                                        <i className="fas fa-user-circle text-slate-300"></i>
                                        <span>Portal Settings</span>
                                    </Link>
                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center space-x-3 px-6 py-3 text-sm font-bold text-rose-600 hover:bg-rose-50 transition-colors"
                                    >
                                        <i className="fas fa-sign-out-alt"></i>
                                        <span>Terminate Session</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <div className="flex flex-1 relative">
                {/* Collapsible Sidebar */}
                <aside
                    className={`bg-white border-r border-gray-200 fixed top-16 bottom-0 overflow-hidden transition-all duration-300 z-40 hidden lg:block ${isSidebarCollapsed ? 'w-20' : 'w-64'
                        }`}
                >
                    <nav className="p-4 space-y-8 overflow-y-auto max-h-[calc(100vh-64px)] scrollbar-hide">
                        {navigationGroups.map((group) => (
                            <div key={group.title} className="space-y-4">
                                <h3 className={`text-[9px] font-black uppercase tracking-[0.3em] text-slate-500 px-4 transition-opacity duration-300 whitespace-nowrap ${isSidebarCollapsed ? 'opacity-0' : 'opacity-100'}`}>
                                    {group.title}
                                </h3>
                                <div className="space-y-1">
                                {group.items.map((item) => (
                                        isSidebarCollapsed ? (
                                            <PremiumTooltip key={item.path} text={item.label} position="right">
                                                <Link
                                                    to={item.path}
                                                    className={`flex items-center rounded-2xl transition-all duration-200 group relative px-4 py-3.5 ${isActive(item.path)
                                                        ? 'bg-[#18216D] text-white shadow-xl shadow-indigo-900/20 font-black'
                                                        : 'text-slate-600 hover:bg-slate-50 hover:text-[#18216D]'
                                                        }`}
                                                >
                                                    <div className="flex items-center justify-center min-w-[24px]">
                                                        <i className={`fas ${item.icon} text-lg w-6 text-center ${isActive(item.path) ? 'text-white' : 'text-slate-300 group-hover:text-[#18216D]'}`}></i>
                                                    </div>
                                                    <span className="ml-3 text-[10px] uppercase font-black tracking-widest transition-opacity duration-300 whitespace-nowrap opacity-0 lg:hidden">
                                                        {item.label}
                                                    </span>
                                                    {isSidebarCollapsed && item.badge > 0 && (
                                                        <div className="absolute top-2 right-2 w-2 h-2 bg-[#FFC425] rounded-full"></div>
                                                    )}
                                                    {isSidebarCollapsed && isActive(item.path) && (
                                                        <div className="absolute left-0 w-1.5 h-6 bg-[#FFC425] rounded-r-full"></div>
                                                    )}
                                                </Link>
                                            </PremiumTooltip>
                                        ) : (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                className={`flex items-center rounded-2xl transition-all duration-200 group relative px-4 py-3.5 ${isActive(item.path)
                                                    ? 'bg-[#18216D] text-white shadow-xl shadow-indigo-900/20 font-black'
                                                    : 'text-slate-600 hover:bg-slate-50 hover:text-[#18216D]'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-center min-w-[24px]">
                                                    <i className={`fas ${item.icon} text-lg w-6 text-center ${isActive(item.path) ? 'text-white' : 'text-slate-300 group-hover:text-[#18216D]'}`}></i>
                                                </div>
                                                <span className="ml-3 text-[10px] uppercase font-black tracking-widest transition-opacity duration-300 whitespace-nowrap opacity-100">
                                                    {item.label}
                                                </span>
                                                {item.badge > 0 && (
                                                    <span className="ml-auto bg-[#FFC425] text-[#18216D] text-[9px] font-black px-1.5 py-0.5 rounded-full animate-pulse">
                                                        {item.badge}
                                                    </span>
                                                )}
                                            </Link>
                                        )
                                ))}
                                </div>
                            </div>
                        ))}
                    </nav>
                </aside>

                {/* Main Content Area */}
                <main
                    className={`flex-1 p-8 min-h-screen min-w-0 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
                        }`}
                >
                    {children}
                </main>
                <FloatingManualButton url="/manuals/Admin_Dashboard_User_Manual.pdf" />
            </div>
        </div>
    );
};

export default AdminLayout;
